import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Weather.css";

interface WeatherItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Weather: React.FC = () => {
  const [data, setData] = useState<WeatherItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/weather`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching weather:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: WeatherItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: WeatherItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Weather",
    de: "Wetter",
    ta: "வானிலை",
    si: "කාලගුණය",
    hi: "मौसम",
    ar: "الطقس",
    tr: "Hava Durumu",
    ru: "Погода",
    zh: "天气",
    fr: "Météo",
    es: "Tiempo",
  };

  const subtitleMap = {
    en: "Weather in German",
    de: "Wetter auf Deutsch",
    ta: "ஜெர்மன் மொழியில் வானிலை",
    si: "ජර්මන් භාෂාවේ කාලගුණය",
    hi: "जर्मन में मौसम",
    ar: "الطقس بالألمانية",
    tr: "Almanca'da Hava Durumu",
    ru: "Погода на немецком",
    zh: "德语中的天气",
    fr: "Météo en allemand",
    es: "Tiempo en alemán",
  };

  return (
    <div className="weather-page-container">
      <div className="weather-page-header">
        <div>
          <h1 className="weather-page-title">{titleMap["de"]}</h1>
          <p className="weather-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="weather-page-loading">Loading...</div>
      ) : (
        <div className="weather-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;

            return (
              <div
                key={item.id}
                className={`weather-page-card ${
                  isPlaying ? "weather-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="weather-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="weather-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="weather-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Weather;
